import React, { useState } from "react";
import { constructQueryParamsString, showErrorToast } from "../../../../../utils";
import { Box, Flex, Text, useToast } from "@chakra-ui/react";
import { useGetSuppliersYearlyData } from "../../../../../hooks/query";
import { calculateLimeGradientColor } from "../../../../ISSB/Dashboard/dashboardDataUtils";
import { nivo_theme } from "../../../../ISSB/Dashboard/components/chart-utils";
import { ResponsiveHeatMap } from "@nivo/heatmap";
import LocalSpinner from "../../../../../components/LocalSpinner";
import { ArrowRight, ArrowUp } from "@phosphor-icons/react";

const TrendVisual = ({ filters }) => {
  const toast = useToast();
  const [yearByYearEmissionsData, setYearByYearEmissionsData] = useState([]);

  const getQueryParamsString = () => {
    let year = filters?.year;
    if (!year) year = new Date().getFullYear();
    const currentYear = new Date().getFullYear();
    const yearsSet = new Set([currentYear, year]);
    let yearToAdd = currentYear - 1;
    while (yearsSet.size < 5 && yearToAdd >= 2021) {
      yearsSet.add(yearToAdd);
      yearToAdd--;
    }
    const years = Array.from(yearsSet).sort((a, b) => b - a);
    return constructQueryParamsString({ years, categories: filters?.categories, dataQuality: filters?.dataQuality });
  };

  const constructHeatMapData = (suppliers) => {
    const yearCompletionMap = {};
    const supplierMap = {};

    suppliers.forEach(({ yearlyEmissionData, supplierId, supplierName }) => {
      const verifiedYears = Object.values(yearlyEmissionData || {}).filter((y) => y?.dataQuality === "VERIFIED").length;
      const completedYears = Object.values(yearlyEmissionData || {}).filter((y) => y?.totalCo2Emissions > 0).length;
      const xKey = verifiedYears;
      const countKey = completedYears;

      if (!yearCompletionMap[countKey]) yearCompletionMap[countKey] = {};
      if (!yearCompletionMap[countKey][xKey]) yearCompletionMap[countKey][xKey] = 0;
      if (!supplierMap[countKey]) supplierMap[countKey] = {};
      if (!supplierMap[countKey][xKey]) supplierMap[countKey][xKey] = [];

      yearCompletionMap[countKey][xKey] += 1;
      supplierMap[countKey][xKey].push({ supplierId, supplierName });
    });

    const allValues = Object.values(yearCompletionMap).flatMap((o) => Object.values(o));
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);

    return Object.entries(yearCompletionMap).map(([yearsCompleted, xMap]) => ({
      id: `${yearsCompleted}`,
      data: Object.entries(xMap).map(([verifiedYears, value]) => ({
        x: `${verifiedYears}`,
        y: value,
        color: calculateLimeGradientColor(value, minValue, maxValue),
        suppliers: supplierMap[yearsCompleted][verifiedYears] || [],
      })),
    }));
  };

  const { isFetching } = useGetSuppliersYearlyData(getQueryParamsString(), {
    onSuccess: (data) => {
      const filteredData = data.filter((item) => !item.excludeFromReport);
      setYearByYearEmissionsData(constructHeatMapData(filteredData));
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  if (isFetching) return <LocalSpinner />;

  return (
    <Box>
      <Flex height="500px" w="100%">
        <Box flex="1" minW={0}>
          <ResponsiveHeatMap
            data={yearByYearEmissionsData}
            margin={{ top: 60, right: 20, bottom: 20, left: 120 }}
            valueFormat=".0f"
            axisTop={{
              tickSize: 0,
              tickPadding: 0,
              legend: "",
              legendPosition: "middle",
              legendOffset: -50,
              truncateTickAt: 0,
              renderTick: (tick) => (
                <g transform={`translate(${tick.x},${tick.y - 32})`}>
                  <path d="M-24,-12 L24,-12 Q36,-12 36,0 L36,8 L0,20 L-36,8 L-36,0 Q-36,-12 -24,-12 Z" fill="#051F23" />
                  <text y={-2} textAnchor="middle" dominantBaseline="central" style={{ fill: "#FFFFFF", fontSize: 12, fontWeight: 600, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                    {tick.value}
                  </text>
                </g>
              ),
            }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 0,
              tickRotation: 0,
              legend: "",
              legendPosition: "middle",
              legendOffset: -70,
              truncateTickAt: 0,
              renderTick: (tick) => (
                <g transform={`translate(${tick.x - 60},${tick.y})`}>
                  <path d="M-43,-12 L43,-12 L55,0 L43,12 L-43,12 Q-55,12 -55,0 Q-55,-12 -43,-12 Z" fill="#85C22A" />
                  <text x={-6} textAnchor="middle" dominantBaseline="central" style={{ fill: "#FFFFFF", fontSize: 12, fontWeight: 600, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                    {tick.value}
                  </text>
                </g>
              ),
            }}
            axisRight={{ tickSize: 0, tickPadding: 0, tickValues: [] }}
            axisBottom={{ tickSize: 0, tickPadding: 0, tickValues: [] }}
            colors={(d) => d.data?.color?.color ?? "#F7FAFC"}
            labelTextColor={(d) => d.data?.color?.textColor ?? "#E2E8F0"}
            borderWidth={2}
            borderColor="#FFFFFF"
            emptyColor="#F7FAFC"
            theme={nivo_theme}
            tooltip={({ cell }) => <CustomTooltip suppliers={cell?.data?.suppliers} />}
          />
        </Box>

        {/* Vertical "Complete" indicator */}
        <Flex direction="column" align="center" gap={2} pt="60px" pb="20px">
          <ArrowUp size={14} color="#85C22A" weight="bold" />
          <Box flex="1" w="1px" bg="#85C22A" />
          <Text fontSize={14} fontWeight="bold" whiteSpace="nowrap" color="lime" sx={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>
            Complete
          </Text>
        </Flex>
      </Flex>

      {/* Horizontal "Years Verified" axis */}
      <Flex align="center" ml="120px" mr="40px" gap={2} mt={-4}>
        <Text fontSize={14} fontWeight="bold" whiteSpace="nowrap" color="darkTeal">Years Verified</Text>
        <Box flex="1" h="1px" bg="gray.400" />
        <ArrowRight size={14} color="#A0AEC0" weight="bold" />
      </Flex>
    </Box>
  );
};

const CustomTooltip = ({ suppliers }) => (
  <Flex p={2} gap={0} direction="column" borderRadius="md" bg="pureWhite" border="1px solid" borderColor="lime">
    {suppliers?.map((s) => (
      <Text m={0} key={s?.supplierId} p={1} color="darkTeal" fontSize="sm">
        {s?.supplierName}
      </Text>
    ))}
  </Flex>
);

export default TrendVisual;
